import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../hooks/useTheme';
import { useOAuthConnect } from '../hooks/useOAuthConnect';
import { useToast } from '../hooks/useToast';
import { logout, authFetch } from '../services/api';
import { settingsService } from '../services/settingsService';
import githubIcon from '../github (1).png';
import bitbucketIcon from '../bitbucket (1).png';
import gitlabIcon from '../gitlab.png';

const FRAMEWORKS = ['React', 'Vue', 'Angular', 'Node', 'Python'];
const INTEGRATIONS = [
  { key: 'github', label: 'GitHub', icon: githubIcon, oauth: true, invertInDark: true },
  { key: 'gitlab', label: 'GitLab', icon: gitlabIcon, oauth: false },
  { key: 'bitbucket', label: 'Bitbucket', icon: bitbucketIcon, oauth: true },
  { key: 'vercel', label: 'Vercel', icon: '▲', oauth: false, isEmoji: true },
];
const THEMES = [
  { value: 'dark', label: 'Dark' },
  { value: 'light', label: 'Light' },
];

// Must match backend's MAX_IMAGE_SIZE (backend/api/uploads.py)
const MAX_IMAGE_SIZE_MB = 5;
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;

export default function SettingsPage({ onBack, onLogout, embedded = false }) {
  const { theme } = useTheme();
  const toast = useToast();
  const handleLogout = onLogout || (() => { window.location.href = '/login'; });

  const [user, setUser] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [loadingIntegrations, setLoadingIntegrations] = useState(true);

  const loadIntegrations = useCallback(() => {
    setLoadingIntegrations(true);
    settingsService.getIntegrations()
      .then(setIntegrations)
      .catch(() => {})
      .finally(() => setLoadingIntegrations(false));
  }, []);

  useEffect(() => {
    settingsService.getMe()
      .then(setUser)
      .catch(() => {})
      .finally(() => setLoadingUser(false));
    loadIntegrations();
  }, [loadIntegrations]);

  const isAdmin = user?.role === 'admin';

  return (
    <div className="ps-page">
      <style>{CSS}</style>

      <div className="ps-header">
        <h2 className="ps-title">Profile Settings</h2>
        <p className="ps-desc">{isAdmin ? 'Manage your account and theme' : 'Manage your account, integrations, and preferences'}</p>
      </div>

      {loadingUser ? (
        <div className="ps-loading">Loading...</div>
      ) : (
        <>
          {/* Profile Card */}
          <ProfileCard user={user} onUserUpdate={setUser} />

          {/* Change Password */}
          <ChangePasswordCard />

          {/* Integrations — user only */}
          {!isAdmin && (
            <IntegrationsCard
              status={integrations}
              loading={loadingIntegrations}
              onRefresh={loadIntegrations}
            />
          )}

          {/* Preferences — admin sees only theme, user sees framework + theme */}
          <PreferencesCard isAdmin={isAdmin} />

          {/* Danger Zone — only for non-admin users */}
          {!isAdmin && <DangerZoneCard onLogout={handleLogout} />}
        </>
      )}
    </div>
  );
}

function ProfileCard({ user, onUserUpdate }) {
  const toast = useToast();
  const [uploading, setUploading] = useState(false);

  if (!user) return null;

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file after a failed attempt
    if (!file) return;

    // IMAGE-UPLOAD-SIZE-FIX: same client-side check as AdminBlogManagement's
    // cover-image upload — must match backend's MAX_IMAGE_SIZE (uploads.py).
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      toast.error(`Image too large — max ${MAX_IMAGE_SIZE_MB}MB (this file is ${(file.size / 1024 / 1024).toFixed(1)}MB)`);
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await authFetch('/api/upload/image', { method: 'POST', body: formData });
      if (!uploadRes.ok) {
        const err = await uploadRes.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      const { url } = await uploadRes.json();

      const patchRes = await authFetch('/api/user/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ avatar_url: url }),
      });
      if (!patchRes.ok) throw new Error('Save failed');

      onUserUpdate((prev) => ({ ...prev, avatar_url: url }));
      toast.success('Profile picture updated');
    } catch (err) {
      toast.error(err.message || 'Failed to update profile picture');
    }
    setUploading(false);
  };

  return (
    <div className="ps-card">
      <h3 className="ps-card-title">Profile</h3>
      <div className="ps-profile-row">
        <label className="ps-avatar-wrap" title="Change profile picture">
          {user.avatar_url ? (
            <img src={user.avatar_url} alt="" className="ps-avatar ps-avatar--img" />
          ) : (
            <div className="ps-avatar">{user.email[0].toUpperCase()}</div>
          )}
          <span className="ps-avatar-edit">{uploading ? '…' : '✎'}</span>
          <input type="file" accept="image/*" onChange={handleAvatarChange} hidden disabled={uploading} />
        </label>
        <div className="ps-profile-info">
          <div className="ps-profile-email">{user.email}</div>
          <div className="ps-profile-meta">
            Role: <span className="ps-profile-role">{user.role}</span>
          </div>
          <div className="ps-profile-meta">
            Member since {new Date(user.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>
      </div>
    </div>
  );
}

function ChangePasswordCard() {
  const toast = useToast();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (newPassword.length < 8) { toast.error('Min 8 characters'); return; }
    setSaving(true);
    try {
      await settingsService.changePassword({ current_password: currentPassword, new_password: newPassword });
      toast.success('Password updated');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  return (
    <div className="ps-card">
      <h3 className="ps-card-title">Change Password</h3>
      <form onSubmit={handleSubmit} className="ps-form">
        <div className="ps-field">
          <label className="ps-label">Current password</label>
          <input type="password" className="ps-input" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required />
        </div>
        <div className="ps-field">
          <label className="ps-label">New password</label>
          <input type="password" className="ps-input" value={newPassword} onChange={e => setNewPassword(e.target.value)} minLength={8} required />
        </div>
        <div className="ps-field">
          <label className="ps-label">Confirm new password</label>
          <input type="password" className="ps-input" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} minLength={8} required />
        </div>
        <button type="submit" className="ps-btn" disabled={saving}>
          {saving ? 'Updating…' : 'Update Password'}
        </button>
      </form>
    </div>
  );
}

function IntegrationsCard({ status, loading, onRefresh }) {
  const toast = useToast();
  const [patInputs, setPatInputs] = useState({});
  const [busy, setBusy] = useState(null);

  const { startOAuth, connecting } = useOAuthConnect(
    async (provider) => { toast.success(`${provider} connected`); onRefresh?.(); },
    (msg) => toast.error(msg),
  );

  const handleSavePat = async (provider) => {
    const token = (patInputs[provider] || '').trim();
    if (!token) { toast.error('Enter a token first'); return; }
    setBusy(provider);
    try {
      await settingsService.connectIntegration(provider, token);
      toast.success(`${provider} connected`);
      setPatInputs(p => ({ ...p, [provider]: '' }));
      onRefresh?.();
    } catch (e) { toast.error(e.message); }
    setBusy(null);
  };

  const handleDisconnect = async (provider) => {
    setBusy(provider);
    try {
      await settingsService.disconnectIntegration(provider);
      toast.success(`${provider} disconnected`);
      onRefresh?.();
    } catch (e) { toast.error(e.message); }
    setBusy(null);
  };

  return (
    <div className="ps-card">
      <h3 className="ps-card-title">Integrations</h3>
      {loading ? (
        <div className="ps-loading">Loading integrations...</div>
      ) : (
        <div className="ps-integrations-grid">
          {INTEGRATIONS.map(({ key, label, icon, oauth, isEmoji, invertInDark }) => {
            const connected = status?.[key] ?? false;
            const isBusy = busy === key || connecting === key;
            return (
              <div key={key} className="ps-integration-item">
                {!isEmoji ? (
                  <img src={icon} alt={label} className={`ps-integration-icon${invertInDark ? ' ps-icon-invert' : ''}`} />
                ) : (
                  <span className="ps-integration-emoji">{icon}</span>
                )}
                <div className="ps-integration-body">
                  <div className="ps-integration-name">{label}</div>
                  <div className={`ps-integration-status${connected ? ' ps-integration-status--on' : ''}`}>
                    {connected ? '✓ Connected' : '✕ Not Connected'}
                  </div>
                  {connected ? (
                    <button className="ps-btn-danger-sm" onClick={() => handleDisconnect(key)} disabled={isBusy}>
                      {isBusy ? '…' : 'Disconnect'}
                    </button>
                  ) : oauth ? (
                    <button className="ps-btn-sm" onClick={() => startOAuth(key)} disabled={isBusy}>
                      {isBusy ? 'Connecting…' : 'Connect'}
                    </button>
                  ) : (
                    <div className="ps-pat-row">
                      <input
                        type="password" placeholder="Access token" className="ps-input ps-input--sm"
                        value={patInputs[key] || ''}
                        onChange={e => setPatInputs(p => ({ ...p, [key]: e.target.value }))}
                      />
                      <button className="ps-btn-sm" onClick={() => handleSavePat(key)} disabled={isBusy}>
                        {isBusy ? '…' : 'Save'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PreferencesCard({ isAdmin }) {
  const toast = useToast();
  const { mode, setMode } = useTheme();
  const [prefs, setPrefs] = useState({ defaultFramework: 'React', theme: mode });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    settingsService.getPreferences()
      .then(p => setPrefs({ defaultFramework: p.defaultFramework || 'React', theme: p.theme === 'light' ? 'light' : 'dark' }))
      .catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await settingsService.savePreferences(prefs);
      setMode(prefs.theme);
      toast.success('Preferences saved');
    } catch (e) { toast.error('Failed: ' + e.message); }
    setSaving(false);
  };

  return (
    <div className="ps-card">
      <h3 className="ps-card-title">Preferences</h3>
      <div className="ps-form">
        {!isAdmin && (
          <div className="ps-field">
            <label className="ps-label">Default Framework</label>
            <select className="ps-select" value={prefs.defaultFramework} onChange={e => setPrefs(p => ({ ...p, defaultFramework: e.target.value }))}>
              {FRAMEWORKS.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
        )}
        <div className="ps-field">
          <label className="ps-label">Theme</label>
          <select className="ps-select" value={prefs.theme} onChange={e => { const v = e.target.value; setPrefs(p => ({ ...p, theme: v })); setMode(v); }}>
            {THEMES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <button className="ps-btn" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save Preferences'}
        </button>
      </div>
    </div>
  );
}

function DangerZoneCard({ onLogout }) {
  const toast = useToast();
  const [showModal, setShowModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await settingsService.deleteAccount();
      toast.success('Account deleted');
      logout();
      onLogout();
    } catch (e) {
      toast.error('Failed: ' + e.message);
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="ps-card ps-card--danger">
        <h3 className="ps-card-title ps-card-title--danger">Danger Zone</h3>
        <p className="ps-danger-desc">Permanently delete your account and all associated data.</p>
        <button className="ps-btn-danger" onClick={() => setShowModal(true)}>Delete Account</button>
      </div>

      {showModal && (
        <div className="ps-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="ps-modal" onClick={e => e.stopPropagation()}>
            <h3 className="ps-modal-title">⚠️ Delete Account</h3>
            <p className="ps-modal-desc">This action cannot be undone. All projects, jobs, and settings will be permanently deleted.</p>
            <div className="ps-modal-actions">
              <button className="ps-btn-secondary" onClick={() => setShowModal(false)} disabled={deleting}>Cancel</button>
              <button className="ps-btn-danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Yes, Delete My Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const CSS = `
.ps-page {
  padding: 32px 0; max-width: 1100px; margin: 0 auto;
  position: relative;
}

.ps-header { margin-bottom: 24px; }
.ps-title { font-size: 20px; font-weight: 700; color: var(--text-primary); margin: 0 0 4px; }
.ps-desc { font-size: 13px; color: var(--text-muted); margin: 0; }
.ps-loading { text-align: center; color: var(--text-muted); padding: 40px 0; font-size: 14px; }

/* Card — matches dashboard aa-chart-card */
.ps-card {
  position: relative;
  padding: 24px; border-radius: 18px; margin-bottom: 20px;
  background: linear-gradient(155deg, #12152b 0%, #0c0f22 100%);
  border: 1px solid rgba(99,102,241,0.13);
  box-shadow: 0 1px 3px rgba(0,0,0,0.6), 0 8px 32px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06);
  overflow: hidden;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}
.ps-card::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 40%, rgba(139,92,246,0.2) 100%);
  border-radius: 18px 18px 0 0; opacity: 0.8;
}
.ps-card:hover {
  border-color: rgba(99,102,241,0.24);
  box-shadow: 0 2px 8px rgba(0,0,0,0.5), 0 14px 40px rgba(0,0,0,0.32), 0 0 0 1px rgba(99,102,241,0.1), inset 0 1px 0 rgba(255,255,255,0.08);
}
[data-theme="light"] .ps-card {
  background: #ffffff;
  border-color: rgba(99,102,241,0.11);
  box-shadow: 0 1px 3px rgba(15,23,42,0.06), 0 6px 24px rgba(15,23,42,0.05);
}
[data-theme="light"] .ps-card::before { opacity: 0.55; }
[data-theme="light"] .ps-card:hover {
  border-color: rgba(99,102,241,0.2);
  box-shadow: 0 2px 8px rgba(15,23,42,0.07), 0 12px 32px rgba(99,102,241,0.06), 0 0 0 1px rgba(99,102,241,0.07);
}
.ps-card--danger {
  background: linear-gradient(155deg, #1a0a0a 0%, #110608 100%);
  border-color: rgba(239,68,68,0.22);
  box-shadow: 0 1px 3px rgba(0,0,0,0.6), 0 8px 32px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.04);
}
.ps-card--danger::before {
  background: linear-gradient(90deg, #ef4444 0%, #f87171 40%, rgba(248,113,113,0.2) 100%);
}
[data-theme="light"] .ps-card--danger { background: #fff5f5; border-color: rgba(239,68,68,0.18); }

.ps-card-title { font-size: 15px; font-weight: 700; color: var(--text-primary); margin: 0 0 16px; }
.ps-card-title--danger { color: #ef4444; }

/* Profile */
.ps-profile-row { display: flex; align-items: center; gap: 16px; }
.ps-avatar {
  width: 48px; height: 48px; border-radius: 50%; flex-shrink: 0;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  display: flex; align-items: center; justify-content: center;
  font-size: 20px; font-weight: 700; color: #fff;
}
.ps-avatar--img { object-fit: cover; }
.ps-avatar-wrap { position: relative; cursor: pointer; flex-shrink: 0; display: inline-block; }
.ps-avatar-edit {
  position: absolute; bottom: -2px; right: -2px; width: 18px; height: 18px;
  border-radius: 50%; background: #1e293b; border: 2px solid var(--bg-primary, #0f172a);
  display: flex; align-items: center; justify-content: center;
  font-size: 9px; color: #fff;
}
[data-theme="light"] .ps-avatar-edit { background: #6366f1; border-color: #fff; }
.ps-profile-info { display: flex; flex-direction: column; gap: 4px; }
.ps-profile-email { font-size: 15px; font-weight: 600; color: var(--text-primary); }
.ps-profile-meta { font-size: 12px; color: var(--text-muted); }
.ps-profile-role { font-weight: 600; color: var(--text-primary); text-transform: capitalize; }

/* Form */
.ps-form { display: flex; flex-direction: column; gap: 14px; }
.ps-field { display: flex; flex-direction: column; gap: 6px; }
.ps-label { font-size: 11px; font-weight: 700; color: var(--text-muted); letter-spacing: 0.03em; }
.ps-input {
  padding: 10px 14px; border-radius: 8px; font-size: 13px; max-width: 360px;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.12);
  color: var(--text-primary); outline: none;
}
[data-theme="light"] .ps-input { background: #f8fafc; border-color: rgba(99,102,241,0.15); }
.ps-input:focus { border-color: rgba(99,102,241,0.5); }
.ps-input--sm { padding: 6px 10px; font-size: 11px; max-width: 160px; }
.ps-select {
  padding: 10px 14px; border-radius: 8px; font-size: 13px; max-width: 280px;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.12);
  color: var(--text-primary); outline: none; cursor: pointer;
}
[data-theme="light"] .ps-select { background: #f8fafc; border-color: rgba(99,102,241,0.15); }

/* Buttons */
.ps-btn {
  padding: 10px 20px; border-radius: 8px; font-size: 13px; font-weight: 600;
  background: linear-gradient(135deg, #6366f1, #8b5cf6); border: none;
  color: #fff; cursor: pointer; width: fit-content;
}
.ps-btn:hover { box-shadow: 0 4px 12px rgba(99,102,241,0.3); }
.ps-btn-sm {
  padding: 6px 12px; border-radius: 6px; font-size: 11px; font-weight: 600;
  background: linear-gradient(135deg, #6366f1, #8b5cf6); border: none;
  color: #fff; cursor: pointer; white-space: nowrap; flex-shrink: 0;
}
.ps-btn-secondary {
  padding: 10px 20px; border-radius: 8px; font-size: 13px; font-weight: 600;
  background: transparent; border: 1px solid rgba(255,255,255,0.12);
  color: var(--text-secondary); cursor: pointer;
}
[data-theme="light"] .ps-btn-secondary { border-color: rgba(99,102,241,0.15); }
.ps-btn-danger {
  padding: 10px 20px; border-radius: 8px; font-size: 13px; font-weight: 600;
  background: #ef4444; border: none; color: #fff; cursor: pointer;
}
.ps-btn-danger-sm {
  padding: 6px 12px; border-radius: 6px; font-size: 11px; font-weight: 600;
  background: transparent; border: 1px solid rgba(239,68,68,0.3);
  color: #f87171; cursor: pointer;
}
[data-theme="light"] .ps-btn-danger-sm { color: #dc2626; }

/* Integrations */
.ps-integrations-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 12px; }
.ps-integration-item {
  position: relative;
  display: flex; align-items: flex-start; gap: 12px; padding: 14px;
  background: linear-gradient(155deg, #0e1124 0%, #090c1a 100%);
  border: 1px solid rgba(99,102,241,0.13);
  border-radius: 14px; overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.5), 0 4px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.04);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.ps-integration-item::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 40%, rgba(139,92,246,0.2) 100%);
  border-radius: 14px 14px 0 0; opacity: 0.7;
}
.ps-integration-item:hover {
  border-color: rgba(99,102,241,0.24);
  box-shadow: 0 2px 8px rgba(0,0,0,0.5), 0 8px 24px rgba(0,0,0,0.3), 0 0 0 1px rgba(99,102,241,0.08), inset 0 1px 0 rgba(255,255,255,0.06);
}
[data-theme="light"] .ps-integration-item {
  background: #ffffff; border-color: rgba(99,102,241,0.11);
  box-shadow: 0 1px 3px rgba(15,23,42,0.06), 0 4px 12px rgba(15,23,42,0.04);
}
[data-theme="light"] .ps-integration-item::before { opacity: 0.5; }
.ps-integration-icon { width: 22px; height: 22px; object-fit: contain; flex-shrink: 0; }
.ps-icon-invert { filter: invert(1) brightness(2); }
[data-theme="light"] .ps-icon-invert { filter: none; }
.ps-integration-emoji { font-size: 22px; flex-shrink: 0; }
.ps-integration-body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 6px; overflow: hidden; }
.ps-integration-name { font-size: 13px; font-weight: 600; color: var(--text-primary); }
.ps-integration-status { font-size: 11px; font-weight: 600; color: #ef4444; }
.ps-integration-status--on { color: #22c55e; }
.ps-pat-row { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
.ps-pat-row .ps-input--sm { flex: 1; min-width: 80px; max-width: none; }

/* Danger */
.ps-danger-desc { font-size: 13px; color: var(--text-muted); margin: 0 0 16px; }

/* Modal */
.ps-modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 1000;
  display: flex; align-items: center; justify-content: center; backdrop-filter: blur(4px);
}
.ps-modal {
  position: relative;
  background: linear-gradient(155deg, #12152b 0%, #0c0f22 100%);
  border: 1px solid rgba(99,102,241,0.22); border-radius: 20px;
  padding: 28px; width: 420px; max-width: 90vw; overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.7), 0 16px 56px rgba(0,0,0,0.55), 0 0 0 1px rgba(99,102,241,0.08), inset 0 1px 0 rgba(255,255,255,0.07);
}
.ps-modal::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 40%, rgba(139,92,246,0.2) 100%);
  border-radius: 20px 20px 0 0; opacity: 0.9;
}
[data-theme="light"] .ps-modal {
  background: #ffffff; border-color: rgba(99,102,241,0.14);
  box-shadow: 0 8px 40px rgba(99,102,241,0.12), 0 1px 4px rgba(0,0,0,0.06);
}
[data-theme="light"] .ps-modal::before { opacity: 0.55; }
.ps-modal-title { font-size: 18px; font-weight: 700; color: var(--text-primary); margin: 0 0 12px; }
.ps-modal-desc { font-size: 13px; color: var(--text-muted); line-height: 1.6; margin: 0 0 24px; }
.ps-modal-actions { display: flex; gap: 10px; justify-content: flex-end; }
`;
